import React, { useEffect, useRef, useState } from 'react';
import {
	Accordion,
	Breakpoints,
	Container,
	ContainerPadding,
	Layout,
	Text,
	TextAlignment,
	TokenTextAppearance,
	styled,
} from '@volkswagen-onehub/components-core';
import { COLOR_TOKEN } from '../../../helpers/color-tokens';
import { StyledIcon } from '../ui/styled-icon';

const StyledAccordion = styled(Accordion)<{ isOpen: boolean }>`
	background: ${({ isOpen }: { isOpen: boolean }) => (isOpen ? COLOR_TOKEN.background.card.primary : 'initial')};
	border-top-left-radius: 20px;
	border-top-right-radius: 20px;
	padding-left: var(--size-dynamic0050);
	padding-right: var(--size-dynamic0050);
	padding-top: var(--size-dynamic0130);
	padding-bottom: var(--size-dynamic0130);
	border-bottom: ${({ isOpen }: { isOpen: boolean }) =>
		!isOpen ? 'none' : `2px solid ${COLOR_TOKEN.background.secondary}`};

	& > div > svg {
		background-color: ${COLOR_TOKEN.button.primary.default};
		color: ${COLOR_TOKEN.content.inverted} !important;
		padding: 12px;
		border-radius: 50%;
		box-sizing: content-box;

		&:active,
		&:hover,
		&:focus {
			color: ${COLOR_TOKEN.content.inverted} !important;
		}
	}

	@media (max-width: 700px) {
		& > div > div > div {
			display: flex;
			flex-wrap: nowrap;
			width: 85%;
		}
	}
	@media (max-width: 376px) {
		& > div > div > div {
			width: 100%;
		}
	}
`;

const StyledContainer = styled(Container)<{ isOpen: boolean }>`
	background-color: ${({ isOpen }: { isOpen: boolean }) =>
		isOpen ? COLOR_TOKEN.background.card.primary : 'transparent'};
	border-bottom-left-radius: ${({ isOpen }: { isOpen: boolean }) => (isOpen ? '20px' : '0')};
	border-bottom-right-radius: ${({ isOpen }: { isOpen: boolean }) => (isOpen ? '20px' : '0')};
	overflow: hidden;
	max-height: ${({ isOpen }: { isOpen: boolean }) => (isOpen ? '100%' : '0')};
`;

const GeneralContainer = styled(Container)<{ isOpen: boolean; isPreviousOpen: boolean }>`
	& > div > div > div > div > div {
		border-bottom: ${({ isOpen, isPreviousOpen }: { isOpen: boolean; isPreviousOpen: boolean }) =>
			isOpen || isPreviousOpen ? 'none' : `1px solid ${COLOR_TOKEN.border.divider}`};
	}
`;

const AnimatedContent = styled.div<{ isOpen: boolean; contentHeight: number }>`
	max-height: ${({ isOpen, contentHeight }: { isOpen: boolean; contentHeight: number }) =>
		isOpen ? `${contentHeight}px` : '0'};
	overflow: hidden;
	transition: max-height 0.3s ease-in-out;
`;

const Subtitle = styled(Text)`
	@media (max-width: 375px) {
		display: none;
	}
`;

// Hooks
const UseIs1280 = (minWidth = 1280): boolean => {
	const [is1280, setIs1280] = useState(false);
	useEffect(() => {
		const mediaQuery = window.matchMedia(`(min-width: ${minWidth}px)`);
		const handleChange = () => setIs1280(mediaQuery.matches);
		handleChange();
		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, [minWidth]);
	return is1280;
};

const useIsMobile = (maxWidth = 559): boolean => {
	const [isMobile, setIsMobile] = useState(false);
	useEffect(() => {
		const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`);
		const handleChange = () => setIsMobile(mediaQuery.matches);
		handleChange();
		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, [maxWidth]);
	return isMobile;
};

const useIs375 = (maxWidth = 375): boolean => {
	const [is375, setIs375] = useState(false);
	useEffect(() => {
		const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`);
		const handleChange = () => setIs375(mediaQuery.matches);
		handleChange();
		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, [maxWidth]);
	return is375;
};

interface AccordionStyledProps {
	icon: React.ReactNode;
	title: string;
	subtitle: string;
	children: React.ReactNode;
	isOpen: boolean;
	onToggle: () => void;
	isPreviousOpen: boolean;
}

export const AccordionStyled = ({
	icon,
	title,
	subtitle,
	children,
	isOpen,
	onToggle,
	isPreviousOpen,
}: AccordionStyledProps): React.ReactElement => {
	const [contentHeight, setContentHeight] = useState(0);
	const contentRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const is1280 = UseIs1280();
	const isMobile = useIsMobile();
	const is375 = useIs375();

	useEffect(() => {
		if (contentRef.current) {
			setContentHeight(contentRef.current.scrollHeight);
		}
	}, [isOpen]);

	useEffect(() => {
		const updateHeight = () => {
			if (contentRef.current) {
				setContentHeight(contentRef.current.scrollHeight);
			}
		};

		updateHeight();

		window.addEventListener('resize', updateHeight);

		return () => {
			window.removeEventListener('resize', updateHeight);
		};
	}, [isOpen]);

	return (
		<div ref={containerRef}>
			<GeneralContainer
				stretchContent
				data-testid="general-container"
				isOpen={isOpen}
				isPreviousOpen={isPreviousOpen}
				wrap="always"
			>
				<Layout
					data-testid="myServices-layout"
					appearance={{
						[Breakpoints.default]: [
							{ name: '.', columns: 0 },
							{ name: 'a', columns: 24 },
							{ name: '.', columns: 0 },
						],
						[Breakpoints.b560]: [
							{ name: '.', columns: 0 },
							{ name: 'a', columns: 24 },
							{ name: '.', columns: 1 },
						],
						[Breakpoints.b960]: [
							{ name: '.', columns: 0 },
							{ name: 'a', columns: 24 },
							{ name: '.', columns: 0 },
						],
						[Breakpoints.b1280]: [
							{ name: '.', columns: 0 },
							{ name: 'a', columns: 24 },
							{ name: '.', columns: 0 },
						],
						[Breakpoints.b1920]: [
							{ name: '.', columns: 0 },
							{ name: 'a', columns: 24 },
							{ name: '.', columns: 0 },
						],
					}}
					allowOverflowingContent
				>
					<StyledAccordion
						data-testid="accordion"
						id="uniqueid0"
						headline={
							<Container verticalAlign="flex-start" wrap="never">
								<StyledIcon>{icon}</StyledIcon>
								<Container
									padding={{ left: is375 ? ContainerPadding.static0 : ContainerPadding.dynamic0050 }}
									wrap="always"
									verticalAlign="center"
								>
									<Text
										appearance={is1280 ? TokenTextAppearance.headline300 : TokenTextAppearance.headline200}
										textAlign={TextAlignment.left}
										bold
										wordBreak
									>
										{title}
									</Text>
									<Container
										padding={{ top: ContainerPadding.static150, right: ContainerPadding.static350 }}
										wrap="always"
									>
										<Subtitle appearance={TokenTextAppearance.label200} textAlign={TextAlignment.left} wordBreak>
											{subtitle}
										</Subtitle>
									</Container>
								</Container>
							</Container>
						}
						onClick={onToggle}
						isOpen={isOpen}
					>
						<StyledContainer
							wrap="always"
							padding={{
								top: isMobile ? ContainerPadding.dynamic0130 : ContainerPadding.dynamic0150,
								bottom: isMobile ? ContainerPadding.dynamic0130 : ContainerPadding.dynamic0150,
							}}
							isOpen={isOpen}
						>
							<AnimatedContent isOpen={isOpen} contentHeight={contentHeight}>
								<div ref={contentRef}>{children}</div>
							</AnimatedContent>
						</StyledContainer>
					</StyledAccordion>
				</Layout>
			</GeneralContainer>
		</div>
	);
};
